"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, Cpu, Gauge, ShieldCheck, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const features = [{
  id: "f-1",
  title: "Controller & Operating Modes",
  description: "Compare how the operator controls movement and speed.",
  icon: Cpu
}, {
  id: "f-2",
  title: "Safety Functions",
  description: "Review the documented emergency-stop and rider-safety features for the exact model.",
  icon: ShieldCheck
}, {
  id: "f-3",
  title: "Operating Footprint",
  description: "Confirm the complete space needed for the bull, arena and surrounding clearance.",
  icon: Gauge
}, {
  id: "f-4",
  title: "Parts & Support",
  description: "Check availability of compatible replacement components before purchase.",
  icon: Wrench
}];
export default function Machinery() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="commercial-mechanical-bull-riding-machines" data-nav="light" className="border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Eyebrow and Section Header */}
        <div className="mb-12 border-b border-border pb-8">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground uppercase sm:text-4xl md:text-5xl">
                Commercial Mechanical Bull Riding Machines
              </h2>
            </div>
            <p className="max-w-xl font-sans text-base text-muted-foreground">
              A commercial mechanical bull riding machine needs to fit the way you intend to use it. A machine that stays permanently inside one venue creates different priorities from one that an event operator transports and sets up repeatedly.
            </p>
          </div>
        </div>

        {/* Single Full-Width Stacked Column */}
        <div className="space-y-10 md:space-y-12">
          {/* Engineering Breakdown / What Commercial Buyers Should Compare */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }}>
            <h3 className="font-serif text-2xl font-bold tracking-tight text-foreground uppercase sm:text-3xl">
              What Commercial Buyers Should Compare
            </h3>
            <div className="mt-4 space-y-3 font-sans text-base leading-relaxed text-muted-foreground">
              <p>
                For example, a bar or entertainment venue may focus heavily on operating footprint, controller features and long-term parts availability. An event company may place more importance on packed dimensions, transportation, setup and repeated assembly.
              </p>
              <p>
                Before choosing a commercial bull riding machine, compare the complete system rather than its appearance alone. Look at the controller, motion equipment, safety area, electrical requirements, included components and available support.
              </p>
            </div>
          </motion.div>

          {/* Facility Applications Checklist Card */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          delay: 0.08,
          ease: "easeOut"
        }} className="border border-border bg-card p-6 sm:p-8">
            <h3 className="font-serif text-lg font-semibold tracking-wide text-foreground uppercase">
              Facility Applications
            </h3>
            <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 font-sans text-sm text-muted-foreground">
              <li className="flex items-center gap-3">
                <span className="flex size-5 shrink-0 items-center justify-center bg-primary text-primary-foreground">
                  <Check className="size-3" />
                </span>
                <span>Western bars and entertainment venues</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex size-5 shrink-0 items-center justify-center bg-primary text-primary-foreground">
                  <Check className="size-3" />
                </span>
                <span>Family entertainment centers</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex size-5 shrink-0 items-center justify-center bg-primary text-primary-foreground">
                  <Check className="size-3" />
                </span>
                <span>Resorts, rodeo venues and attractions</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex size-5 shrink-0 items-center justify-center bg-primary text-primary-foreground">
                  <Check className="size-3" />
                </span>
                <span>Event operators</span>
              </li>
            </ul>
          </motion.div>

          {/* Feature Blocks */}
          <div className="space-y-4">
            {features.map((feature, i) => {
            const IconComponent = feature.icon;
            return <motion.div key={feature.id} data-index={i} initial={shouldReduceMotion ? false : {
              opacity: 0,
              y: 12
            }} whileInView={shouldReduceMotion ? undefined : {
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.2
            }} transition={{
              duration: 0.4,
              delay: 0.08 * i,
              ease: "easeOut"
            }} className="border border-border bg-card p-5 transition-[border-color] duration-150 hover:border-foreground">
                  <div className="flex items-start gap-4">
                    <div className="flex size-10 shrink-0 items-center justify-center border border-border bg-muted text-foreground">
                      <IconComponent className="size-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-serif text-lg font-semibold tracking-wide text-foreground uppercase">
                        {feature.title}
                      </h4>
                      <p className="mt-1 font-sans text-sm leading-relaxed text-muted-foreground">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>

          {/* CTA and Guidance Box */}
          <div className="border-t border-border pt-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <Button asChild className="bg-primary px-6 py-5 font-serif text-base font-semibold tracking-wider text-primary-foreground uppercase hover:bg-primary/90">
                <Link to="/RequestQuotePage">
                  Request Product Information
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-foreground px-6 py-5 font-serif text-base font-semibold tracking-wider text-foreground uppercase hover:bg-foreground hover:text-background">
                <Link to="/SetupSafetyAndMaintenancePage">
                  View Setup & Safety
                </Link>
              </Button>
            </div>
            <p className="mt-4 font-mono text-xs text-muted-foreground">
              Shipping arrangements and available documentation vary by machine. Review the individual product information or contact us to confirm what is available for the model you are considering.
            </p>
          </div>
        </div>
      </div>
    </section>;
}